"use client";

import { useState } from "react";
import { m } from "framer-motion";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    budget: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.fallback || data.error || "Something went wrong");
        setSending(false);
        return;
      }

      setSubmitted(true);
      setSending(false);
    } catch {
      setError("Could not send. Email support@mdglabs.dev directly.");
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative px-4 py-24">
      <div className="section-divider mx-auto mb-20 max-w-xl" />

      <div className="mx-auto max-w-3xl">
        <SectionHeader
          index="07"
          code="contact"
          align="center"
          className="mb-12"
          title={<>Ready to Build <span className="gradient-text">Something Great?</span></>}
          blurb="Tell me what you’re trying to do. I’ll reply within 4 hours during the week."
        />

        <AnimatedSection delay={0.15}>
          {submitted ? (
            <div className="glass-card rounded-2xl p-10 text-center ring-1 ring-emerald-500/20">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/20">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white">Message sent</h3>
              <p className="mt-2 text-[#94A3B8]">
                I’ll get back to you within a few hours.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: "", email: "", budget: "", message: "" });
                }}
                className="mt-6 text-sm text-emerald-400 hover:text-emerald-300"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8 ring-1 ring-white/[0.06]">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                    Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-lg bg-white/[0.03] px-4 py-3 text-sm text-white ring-1 ring-white/[0.08] placeholder:text-white/20 focus:outline-none focus:ring-emerald-500/40"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-lg bg-white/[0.03] px-4 py-3 text-sm text-white ring-1 ring-white/[0.08] placeholder:text-white/20 focus:outline-none focus:ring-emerald-500/40"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="budget" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                  Budget Range
                </label>
                <select
                  id="budget"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="w-full rounded-lg bg-white/[0.03] px-4 py-3 text-sm text-white ring-1 ring-white/[0.08] focus:outline-none focus:ring-emerald-500/40 [&>option]:bg-[#0A0A0F]"
                >
                  <option value="">Select a range</option>
                  <option value="Under $5,000">Under $5,000</option>
                  <option value="$5,000 – $10,000">$5,000 – $10,000</option>
                  <option value="$10,000 – $20,000">$10,000 – $20,000</option>
                  <option value="$20,000 – $30,000">$20,000 – $30,000</option>
                  <option value="$30,000+">$30,000+</option>
                  <option value="Not sure yet">Not sure yet</option>
                </select>
              </div>

              <div className="mt-5">
                <label htmlFor="message" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                  What are you building?
                </label>
                <textarea
                  id="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full resize-none rounded-lg bg-white/[0.03] px-4 py-3 text-sm text-white ring-1 ring-white/[0.08] placeholder:text-white/20 focus:outline-none focus:ring-emerald-500/40"
                  placeholder="Describe your project, timeline, and anything else I should know..."
                />
              </div>

              {error && (
                <div className="mt-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400 ring-1 ring-red-500/20">
                  {error}
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <m.button
                  type="submit"
                  disabled={sending}
                  whileHover={sending ? undefined : { scale: 1.02 }}
                  whileTap={sending ? undefined : { scale: 0.98 }}
                  className="rounded-full bg-emerald-500/15 px-8 py-3 text-sm font-medium text-emerald-400 ring-1 ring-emerald-500/30 transition-all hover:bg-emerald-500/25 hover:ring-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending..." : "Send Message →"}
                </m.button>

                <span className="text-center text-xs text-[#94A3B8]/40 sm:text-left">
                  Or email directly: support@mdglabs.dev
                </span>
              </div>
            </form>
          )}
        </AnimatedSection>

        {/* Quick reply promise */}
        <AnimatedSection delay={0.3} className="mt-8 text-center">
          <p className="text-sm text-[#94A3B8]/60">
            I reply to every message within 4 hours during the week. Usually faster.
          </p>
        </AnimatedSection>

        {/* Extra subtle message */}
        <AnimatedSection delay={0.5} className="mt-16 text-center">
          <p className="text-sm text-[#94A3B8]/40 font-[family-name:var(--font-jetbrains-mono)]">
            {"// based in the boston area, working remote everywhere"}
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Contact;
